// features/notifications/notificationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Store the current request's AbortController
let currentFetchController = null;

// Thunks for REST API calls
export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async ({ limit = 20, offset = 0, is_read = null }, { rejectWithValue }) => {
    try {
      // Cancel previous request if it exists
      if (currentFetchController) {
        currentFetchController.abort();
      }

      // Create new AbortController for this request
      currentFetchController = new AbortController();

      const params = { limit, offset };
      if (is_read !== null) {
        // backend expects 'true' / 'false' strings
        params.is_read = is_read ? 'true' : 'false';
      }

      const response = await axiosInstance.get('/api/notifications/', {
        params,
        signal: currentFetchController.signal
      });
      
      // Clear the controller after successful request
      currentFetchController = null;
      
      return response.data;
    } catch (error) {
      // Don't treat abort as an error
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ cancelled: true });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/notifications/${notificationId}/mark_read/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await axiosInstance.post('/api/notifications/mark_all_read/');
      return {};
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark all as read');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/unreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/notifications/unread_count/');
      return response.data.unread_count;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch unread count');
    }
  }
);

export const fetchNotificationCounts = createAsyncThunk(
  'notifications/counts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/notifications/counts/');
      // { total, unread, read }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notification counts');
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,              // for header bell
  loading: false,
  error: null,
  hasMore: true,
  offset: 0,
  currentFilter: 'all',         // Track current filter
  counts: {                    // global tab counts from backend
    total: 0,
    unread: 0,
    read: 0,
  },
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action) => {
      // Add new notification to the beginning
      state.notifications.unshift(action.payload);
      if (!action.payload.is_read) {
        state.unreadCount += 1;
        state.counts.unread += 1;
      }
      state.counts.total += 1;
    },
    updateNotificationRead: (state, action) => {
      const notification = state.notifications.find(n => n.id === action.payload.id);
      if (notification && !notification.is_read) {
        notification.is_read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
        state.counts.unread = Math.max(0, state.counts.unread - 1);
        state.counts.read += 1;
      }
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    clearNotifications: (state) => {
      // Clear the list and reset pagination
      state.notifications = [];
      state.offset = 0;
      state.hasMore = true;
      state.loading = false;
      // leave state.unreadCount and state.counts as is
    },
    setCurrentFilter: (state, action) => {
      // Track which filter is currently active
      state.currentFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        // Ignore cancelled requests
        if (action.payload?.cancelled) {
          state.loading = false;
          return;
        }

        state.loading = false;
        const newNotifications = action.payload.results || action.payload;
        
        if (state.offset === 0) {
          // Replace notifications only if this is a fresh fetch
          state.notifications = newNotifications;
        } else {
          // Append for pagination
          state.notifications.push(...newNotifications);
        }
        
        state.hasMore = action.payload.next !== null;
        state.offset = state.notifications.length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        // Don't set error for cancelled requests
        if (action.payload?.cancelled) {
          state.loading = false;
          return;
        }
        
        state.loading = false;
        state.error = action.payload;
      })
      
      // Mark notification as read
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload.id);
        if (notification && !notification.is_read) {
          notification.is_read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          state.counts.unread = Math.max(0, state.counts.unread - 1);
          state.counts.read += 1;
        }
      })
      
      // Mark all as read
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach(n => n.is_read = true);
        state.counts.read = state.counts.total;
        state.counts.unread = 0;
        state.unreadCount = 0;
      })
      
      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      
      // Fetch notification counts
      .addCase(fetchNotificationCounts.fulfilled, (state, action) => {
        // action.payload = { total, unread, read }
        state.counts = action.payload;
      });
  },
});

export const {
  addNotification,
  updateNotificationRead,
  setUnreadCount,
  clearNotifications,
  setCurrentFilter,
} = notificationSlice.actions;

export default notificationSlice.reducer;